import { useCallback } from "react";
import type { ReactNode } from "react";
import type { AlertsState, Cloud, CoreVersionState, LatestTags, OverviewCluster } from "../api/types";
import { useSort } from "../hooks/useSort";
import { behindLatest } from "../util/versions";
import AlertBadge from "./AlertBadge";
import VersionChip from "./VersionChip";

function coreState(c: OverviewCluster): CoreVersionState | null {
    return c.state.fleet === "core" ? c.state : null;
}

const COLUMNS: { col: string; label: string; sortable: boolean }[] = [
    { col: "status", label: "Status", sortable: false },
    { col: "cluster", label: "Cluster", sortable: true },
    { col: "infra", label: "Infra version", sortable: true },
    { col: "chart", label: "Chart Version", sortable: true },
    { col: "ci", label: "CI Templates", sortable: true },
    { col: "alerts", label: "Alerts", sortable: false },
    { col: "links", label: "Links", sortable: false },
];

export default function CoreTable({
    clusters,
    latestTags,
    alerts,
}: {
    clusters: OverviewCluster[];
    latestTags: LatestTags | null;
    alerts: AlertsState;
}) {
    const sortVal = useCallback((c: OverviewCluster, col: string): string => {
        const s = coreState(c);
        switch (col) {
            case "infra":
                return s?.argots?.version ?? s?.argots?.raw ?? "";
            case "chart":
                return s?.chartVersion?.version ?? s?.ciTemplates?.pricefxClusterApp ?? "";
            case "ci":
                return s?.ciTemplates?.ref ?? "";
            default:
                return c.cluster;
        }
    }, []);
    const { sorted, toggle, arrow } = useSort(clusters, sortVal, "cluster");
    const latest = (component: string) => latestTags?.components[component]?.[0];

    return (
        <div className="table-wrap">
            <table>
                <thead>
                    <tr>
                        {COLUMNS.map((c) => (
                            <th key={c.col} className={c.sortable ? "sortable" : ""} onClick={c.sortable ? () => toggle(c.col) : undefined}>
                                {c.label}
                                {c.sortable ? <span className="sort-arrow">{arrow(c.col)}</span> : null}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((c) => (
                        <CoreRow key={c.cluster} c={c} latest={latest} alerts={alerts} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CoreRow({ c, latest, alerts }: { c: OverviewCluster; latest: (component: string) => string | undefined; alerts: AlertsState }) {
    const s = coreState(c);
    const alert = alerts.summaries.find((a) => a.cluster === c.cluster);
    const links: { label: string; url: string }[] = [];
    if (c.grafanaUrl) links.push({ label: "grafana", url: c.grafanaUrl });
    if (c.radarUrl) links.push({ label: "radar", url: c.radarUrl });
    if (s?.chartVersion?.sourceUrl) links.push({ label: "chart", url: s.chartVersion.sourceUrl });
    if (s?.ciTemplates?.sourceUrl) links.push({ label: "ci", url: s.ciTemplates.sourceUrl });
    if (s?.argots?.sourceUrl) links.push({ label: "infra", url: s.argots.sourceUrl });

    return (
        <tr>
            <td>
                <span className={`status-dot ${c.status}`} title={c.lastError ?? c.status} />
                {c.stale ? (
                    <span className="stale-badge" title={`fetched ${c.fetchedAt}`}>
                        stale
                    </span>
                ) : null}
            </td>
            <td>
                <strong>{c.cluster}</strong>
                {c.cloud ? <span className={`cloud-badge ${c.cloud}`}>{c.cloud.toUpperCase()}</span> : null}
                {s?.argots ? (
                    <span className="ts-badge" title={`Migrated to ArgoTS (${s.argots.raw ?? s.argots.version ?? "?"})`}>
                        TS
                    </span>
                ) : null}
            </td>
            <InfraCell s={s} latest={latest("argocd-ts-cmp")} />
            <ChartCell s={s} cloud={c.cloud} latestChart={latest("pricefx-cluster-app")} />
            <CiCell s={s} latestCi={latest("gitlab-ci-templates")} />
            <td>{alert && alert.total > 0 ? <AlertBadge summary={alert} /> : <span className="muted">—</span>}</td>
            <td>
                {links.length > 0
                    ? links.map((l, i) => (
                          <span key={l.label}>
                              {i > 0 ? " · " : ""}
                              <a className="source-link" href={l.url} target="_blank" rel="noopener noreferrer">
                                  {l.label}
                              </a>
                          </span>
                      ))
                    : "—"}
            </td>
        </tr>
    );
}

function InfraCell({ s, latest }: { s: CoreVersionState | null; latest: string | undefined }) {
    const ref = s?.argots;
    if (!ref || (!ref.version && !ref.raw)) return <td className="version muted">—</td>;
    const ver = ref.version ?? ref.raw;
    return (
        <td className="version">
            <VersionChip version={ver} behind={behindLatest(ver, latest)} title={ref.raw} />
        </td>
    );
}

function ChartCell({ s, cloud, latestChart }: { s: CoreVersionState | null; cloud: Cloud | undefined; latestChart: string | undefined }) {
    // AWS core is not on ArgoCD: the pfx app ships via gitlab-ci-templates, so its
    // chart version is the pricefx-cluster-app (pca) + pg-operator-dbs (pgdb) pair.
    if (cloud === "aws") {
        const ci = s?.ciTemplates;
        if (!ci?.pricefxClusterApp && !ci?.pgOperatorDbs) return <td className="version muted">—</td>;
        return (
            <td className={`version ${behindLatest(ci.pricefxClusterApp, latestChart) ? "behind-cell" : ""}`}>
                {ci.pricefxClusterApp ? <div>{ci.pricefxClusterApp}</div> : null}
                {ci.pgOperatorDbs ? <div className="tag-extra">pgdb: {ci.pgOperatorDbs}</div> : null}
            </td>
        );
    }
    const chart = s?.chartVersion;
    if (!chart) return <td className="version muted">—</td>;
    const ci = s?.ciTemplates;
    let render: ReactNode = null;
    if (ci?.renderVersion) {
        if (ci.renderMatch === "mismatch") render = <span className="mismatch-cell tag-extra"> render: {ci.renderVersion}</span>;
        else if (ci.renderMatch === "ok") render = <span className="ok-extra tag-extra"> render ok</span>;
    }
    return (
        <td className={`version ${behindLatest(chart.version, latestChart) ? "behind-cell" : ""}`}>
            {chart.version}
            {render}
        </td>
    );
}

function CiCell({ s, latestCi }: { s: CoreVersionState | null; latestCi: string | undefined }) {
    const ci = s?.ciTemplates;
    if (!ci) return <td className="version muted">—</td>;
    return <td className={`version ${behindLatest(ci.ref, latestCi) ? "behind-cell" : ""}`}>{ci.ref}</td>;
}
